import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, StatusPill } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type Raw = Record<string, string>;
type Checked = { line: number; raw: Raw; errors: string[]; insert?: Record<string, unknown> };

const CRIT = ['low', 'medium', 'high', 'critical'];
const HEADERS = ['name', 'property', 'category', 'criticality', 'serial_number', 'brand', 'model', 'agent_type', 'manufacture_date'];

export function CsvImport() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Checked[]>([]);
  const [done, setDone] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setDone(null);
    setErr(null);
    const Papa = (await import('papaparse')).default; // lazy
    const [props, cats] = await Promise.all([
      supabase.from('properties').select('id,name,organization_id').is('deleted_at', null),
      supabase.from('asset_categories').select('id,code').is('deleted_at', null),
    ]);
    const propByName = new Map((props.data ?? []).map((p: any) => [String(p.name).toLowerCase(), p]));
    const catByCode = new Map((cats.data ?? []).map((c: any) => [String(c.code).toLowerCase(), c.id]));

    Papa.parse<Raw>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const checked: Checked[] = res.data.map((raw, i) => {
          const errors: string[] = [];
          const name = (raw.name ?? '').trim();
          if (!name) errors.push(t('csv.errName'));
          const prop = propByName.get((raw.property ?? '').trim().toLowerCase());
          if (!(raw.property ?? '').trim()) errors.push(t('csv.errPropertyRequired'));
          else if (!prop) errors.push(t('csv.errPropertyUnknown'));
          const crit = (raw.criticality ?? '').trim().toLowerCase();
          if (crit && !CRIT.includes(crit)) errors.push(t('csv.errCriticality'));
          let categoryId: string | null = null;
          const catCode = (raw.category ?? '').trim().toLowerCase();
          if (catCode) {
            categoryId = catByCode.get(catCode) ?? null;
            if (!categoryId) errors.push(t('csv.errCategory'));
          }
          const mfg = (raw.manufacture_date ?? '').trim();
          if (mfg && Number.isNaN(Date.parse(mfg))) errors.push(t('csv.errDate'));

          const insert =
            errors.length === 0 && prop
              ? {
                  organization_id: prop.organization_id, property_id: prop.id, name,
                  category_id: categoryId, criticality: crit || 'medium',
                  serial_number: (raw.serial_number ?? '').trim() || null,
                  brand: (raw.brand ?? '').trim() || null, model: (raw.model ?? '').trim() || null,
                  agent_type: (raw.agent_type ?? '').trim() || null, manufacture_date: mfg || null,
                }
              : undefined;
          return { line: i + 2, raw, errors, insert };
        });
        setRows(checked);
      },
    });
  }

  const invalid = rows.filter((r) => r.errors.length > 0);
  const valid = rows.filter((r) => r.errors.length === 0);

  function downloadErrors() {
    const lines = ['line,field_values,reasons', ...invalid.map((r) => `${r.line},"${HEADERS.map((h) => r.raw[h] ?? '').join(' | ')}","${r.errors.join('; ')}"`)];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'import-errors.csv';
    a.click();
  }

  async function confirmImport() {
    // all-or-nothing: only when every row is valid; single atomic insert
    setBusy(true);
    setErr(null);
    const { error } = await supabase.from('assets').insert(valid.map((r) => r.insert) as any[]);
    if (error) setErr(error.message);
    else {
      setDone(valid.length);
      setRows([]);
    }
    setBusy(false);
  }

  return (
    <Layout header={<Topbar />}>
      <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4 px-7 py-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('csv.title')}</h1>
        <Card className="flex flex-wrap items-center gap-3 p-5">
          <input type="file" accept=".csv" onChange={onFile} className="text-sm" />
          <span className="text-xs text-muted">{t('csv.columns')}: <span className="font-mono">{HEADERS.join(', ')}</span></span>
        </Card>

        {done !== null && <div className="rounded-md bg-st-done-bg px-4 py-3 text-sm text-st-done-fg">{t('csv.imported', { count: done })}</div>}
        {err && <div className="rounded-md bg-st-overdue-bg px-4 py-3 text-sm text-st-overdue-fg">{err}</div>}

        {rows.length > 0 && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <StatusPill tone="done">{valid.length} {t('csv.valid')}</StatusPill>
              {invalid.length > 0 && <StatusPill tone="overdue">{invalid.length} {t('csv.withErrors')}</StatusPill>}
              {invalid.length > 0 && <Button variant="secondary" onClick={downloadErrors}>{t('csv.downloadErrors')}</Button>}
              <div className="ml-auto">
                <Button variant="accent" disabled={busy || invalid.length > 0 || valid.length === 0} onClick={confirmImport}>
                  {t('csv.confirm', { count: valid.length })}
                </Button>
              </div>
            </div>
            {invalid.length > 0 && <p className="text-xs text-muted">{t('csv.allOrNothing')}</p>}
            <Card className="overflow-x-auto p-0">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="px-4 py-3 font-medium">#</th>
                    {HEADERS.map((h) => <th key={h} className="px-2 py-3 font-medium">{h}</th>)}
                    <th className="px-4 py-3 font-medium">{t('asset.statusCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line} className="border-t border-divider">
                      <td className="px-4 py-2.5 font-mono text-xs text-muted">{r.line}</td>
                      {HEADERS.map((h) => <td key={h} className="px-2 py-2.5">{r.raw[h]}</td>)}
                      <td className="px-4 py-2.5">
                        {r.errors.length === 0 ? <StatusPill tone="done">OK</StatusPill> : <span className="text-xs text-st-overdue-fg">{r.errors.join('; ')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
}
